import React, { useState, useEffect } from "react";
import Contact from "../components/contact";
import Footer from "../components/Footer";
import StarRating from "../components/StarRating";
import { testimonials } from "../data/testimonials";
import {
  ChevronLeft,
  ChevronRight,
  User,
  Building,
  Users,
  Globe,
  ArrowRight,
} from "lucide-react";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";
import { motion } from "framer-motion";

const ArchitecturalDesign = () => {
  // ---------------- STATE ----------------
  const [currentProject, setCurrentProject] = useState(0);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  // Track the active image index for the currently selected project's thumbnail view
  const [currentProjectImageIndex, setCurrentProjectImageIndex] = useState(0);

  // ---------------- HERO IMAGES ----------------
  const heroImages = [
    "https://res.cloudinary.com/dkwsduc5l/image/upload/v1778835296/Regenta_pjxiny.webp",
    "https://res.cloudinary.com/dkwsduc5l/image/upload/v1778835023/Kapish_bpifai.jpg",
    "https://res.cloudinary.com/dkwsduc5l/image/upload/v1778835023/Grassfield_ng6tmp.jpg",
    "https://res.cloudinary.com/dkwsduc5l/image/upload/v1778835022/PR5_1_ttert3.jpg",
    "https://res.cloudinary.com/dkwsduc5l/image/upload/v1778835022/Dhai_dweep_1_htdglg.jpg",
    "https://res.cloudinary.com/dkwsduc5l/image/upload/v1778835024/ChatGPT_Image_Apr_20_2026_03_27_20_PM_towz0v.png",
    "https://res.cloudinary.com/dkwsduc5l/image/upload/v1778835022/PR5_1_ttert3.jpg",
  ];

  // ---------------- HOOKS ----------------
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  // ---------------- AUTO SLIDE ----------------
  useEffect(() => {
    if (!heroImages.length) return;
    const timer = setInterval(() => {
      setCurrentSlide((p) => (p + 1) % heroImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  // ---------------- FETCH ARCHITECTURAL PROJECTS ----------------
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await fetch(
          "https://archpoint.onrender.com/api/architectural-projects"
        );
        const data = await res.json();
        setProjects(data);
        setLoadingProjects(false);
      } catch (err) {
        console.error("Architectural projects error:", err);
        setLoadingProjects(false);
      }
    };
    fetchProjects();
  }, []);

  // Reset image preview index whenever the main project changes
  useEffect(() => {
    setCurrentProjectImageIndex(0);
  }, [currentProject]);

  // ---------------- DATA ----------------
  const milestones = [
    { icon: Building, number: "300+", label: "Projects" },
    { icon: User, number: "20+ Yr", label: "Experience" },
    { icon: Users, number: "100+", label: "Team" },
    { icon: Globe, number: "Global", label: "Services" },
  ];

  const splitNumber = (numStr) => {
    const match = numStr.match(/^(\d+)(.*)$/);
    return match ? { value: parseInt(match[1]), suffix: match[2] } : null;
  };

  // ---------------- HANDLERS ----------------
  const nextSlide = () =>
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  const prevSlide = () =>
    setCurrentSlide(
      (prev) => (prev - 1 + heroImages.length) % heroImages.length
    );
  const nextProject = () =>
    setCurrentProject((p) => (p + 1) % projects.length);
  const prevProject = () =>
    setCurrentProject((p) => (p - 1 + projects.length) % projects.length);

  // ---------------- RENDER ----------------
  if (loadingProjects) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <h2 className="text-2xl text-gray-600">Loading projects...</h2>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ---------------- HERO ---------------- */}
      <section className="relative h-screen overflow-hidden">
        <div className="relative w-full h-full">
          {heroImages.map((imageUrl, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                index === currentSlide ? "opacity-100" : "opacity-0"
              }`}
            >
              <img
                src={imageUrl}
                alt={`Hero ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black bg-opacity-40"></div>
            </div>
          ))}
        </div>

        {/* Hero Content */}
        <div className="absolute inset-0 flex items-center justify-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white text-center tracking-wide">
            <span className="text-yellow-400">Architectural</span>
            <br />
            Design
          </h1>
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-yellow-400 transition-colors duration-300"
        >
          <ChevronLeft size={48} />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-yellow-400 transition-colors duration-300"
        >
          <ChevronRight size={48} />
        </button>

        {/* Dots Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-colors duration-300 ${
                index === currentSlide
                  ? "bg-yellow-400"
                  : "bg-white bg-opacity-50"
              }`}
            />
          ))}
        </div>
      </section>

      {/* ---------------- OUR SERVICES ---------------- */}
      <section className="py-20 px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-800">
                Our <span className="text-yellow-500">Services</span>
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                We provide complete architectural services to make your project
                remarkable.
              </p>
              {showFullDescription && (
                <div className="text-gray-600 leading-relaxed mb-6">
                  <p className="mb-4">
                    At Arch Point, a leading architectural firm, we specialize in
                    creating thoughtfully designed spaces that harmonize
                    aesthetics and functionality. Our design philosophy blends
                    artistic expression with architectural precision—balancing
                    light, material, and form to craft timeless environments that
                    elevate everyday living and reflect the unique vision of each
                    client.
                  </p>
                </div>
              )}
              <button
                onClick={() => setShowFullDescription(!showFullDescription)}
                className="inline-flex items-center px-6 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors duration-300 font-semibold"
              >
                {showFullDescription ? "Read Less" : "Read More"}
                <ArrowRight className="ml-2" size={20} />
              </button>
            </div>
            <Contact />
          </div>
        </div>
      </section>

      {/* Our USP Section */}
      <section className="py-20 px-4 md:px-8 lg:px-16 bg-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-800">
                Our <span className="text-yellow-500">USP</span>
              </h2>
              <h3 className="text-2xl font-semibold mb-4 text-gray-700">
                Quantum Architecture
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Quantum Architecture focuses on the relationship of visual energy
                with built form. We believe architecture is not just about
                creating spaces, but about crafting experiences that resonate
                with human emotions and environmental harmony.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&h=400&fit=crop"
                alt="Modern Architecture"
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-yellow-500 to-transparent opacity-20 rounded-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- MILESTONES ---------------- */}
      <section className="py-20 px-4 md:px-8 lg:px-16 bg-yellow-500" ref={ref}>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-extrabold text-center mb-12 text-white">
            Our Milestones
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {milestones.map((milestone, index) => {
              const IconComponent = milestone.icon;
              const numberParts = splitNumber(milestone.number);
              return (
                <motion.div
                  key={index}
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="bg-white bg-opacity-20 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                    <IconComponent size={32} className="text-white" />
                  </div>
                  <h3 className="text-4xl font-extrabold text-white mb-2 drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)]">
                    {numberParts ? (
                      <>
                        {inView ? (
                          <CountUp end={numberParts.value} duration={2} />
                        ) : (
                          "0"
                        )}
                        {numberParts.suffix}
                      </>
                    ) : (
                      milestone.number
                    )}
                  </h3>
                  <p className="text-white font-bold tracking-wide">
                    {milestone.label}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      {projects.length > 0 && (
        <section className="py-20 px-4 md:px-8 lg:px-16">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-12 text-gray-800">
              Architectural <span className="text-yellow-500">Projects</span>
            </h2>
            <div className="relative mb-8">
              <img
                src={
                  projects[currentProject].images[currentProjectImageIndex] ||
                  projects[currentProject].images[0]
                }
                alt={projects[currentProject].name}
                className="w-full h-[500px] object-cover rounded-2xl shadow-xl transition-all duration-300"
              />
              <div className="absolute inset-0 bg-black/30 rounded-2xl pointer-events-none"></div>
              <div className="absolute bottom-6 left-6 pointer-events-none">
                <h3 className="text-3xl font-bold text-white">
                  {projects[currentProject].name}
                </h3>
              </div>
              <button
                onClick={prevProject}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-yellow-400 bg-black/40 hover:bg-black/60 p-2 rounded-full transition"
              >
                <ChevronLeft size={40} />
              </button>
              <button
                onClick={nextProject}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-yellow-400 bg-black/40 hover:bg-black/60 p-2 rounded-full transition"
              >
                <ChevronRight size={40} />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {projects[currentProject].images.slice(0, 8).map((img, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setCurrentProjectImageIndex(i)}
                  className={`relative overflow-hidden rounded-lg shadow-md transition transform hover:-translate-y-1 focus:outline-none ${
                    currentProjectImageIndex === i
                      ? "ring-4 ring-yellow-500"
                      : "opacity-80 hover:opacity-100"
                  }`}
                >
                  <img
                    src={img}
                    alt={`project-${i}`}
                    className="w-full h-32 object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------------- TESTIMONIALS (shared data) ---------------- */}
      <section className="py-20 px-4 md:px-8 lg:px-16 bg-gray-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-800">
            What Our <span className="text-yellow-500">Clients Say</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-xl">
                <StarRating rating={testimonial.rating} />
                <p className="text-gray-600 mb-6 italic">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">
                      {testimonial.name}
                    </h4>
                    <p className="text-gray-500 text-sm">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ArchitecturalDesign;